import {
  BOOK_FILES_FILTERS,
  BOOK_FORMATS,
  BOOK_SORTS,
  type BookDetailDto,
  type BookListDto,
  type CatalogEntityDto,
  type CatalogEntityKind,
  type CatalogEntityListDto,
  type CatalogFacetsDto,
  MAX_FILTER_VALUES,
  MY_RATING_FILTERS,
  READING_STATUSES,
  type SortOrder,
  sortTitle,
} from "@shelflist/shared";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { ApiError, api, call } from "./api";

/**
 * Multi-value filter in the URL: accepts a single string (links from before multi-value
 * filters) or an array; yields a de-duplicated array of at most 20 values, `undefined` if empty.
 */
function multiFilter(accept: (value: string) => boolean = () => true) {
  return z
    .union([z.string(), z.array(z.string())])
    .transform((raw) => {
      const values = [...new Set(Array.isArray(raw) ? raw : [raw])]
        .filter((value) => value !== "" && accept(value))
        .slice(0, MAX_FILTER_VALUES);
      return values.length > 0 ? values : undefined;
    })
    .optional()
    .catch(undefined);
}

/** `values` plus `value`: no duplicates, and nothing is added beyond {@link MAX_FILTER_VALUES}. */
export function addFilterValue(values: string[] | undefined, value: string): string[] {
  const current = values ?? [];
  if (current.includes(value) || current.length >= MAX_FILTER_VALUES) return current;
  return [...current, value];
}

const isBookFormat = (value: string) => (BOOK_FORMATS as readonly string[]).includes(value);

/** Search params of the library page and of entity detail pages (the URL is the view state). */
export const bookSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  sort: z.enum(BOOK_SORTS).optional().catch(undefined),
  order: z.enum(["asc", "desc"]).optional().catch(undefined),
  author: multiFilter(),
  series: multiFilter(),
  tag: multiFilter(),
  publisher: multiFilter(),
  language: multiFilter(),
  format: multiFilter(isBookFormat),
  myStatus: z.enum(READING_STATUSES).optional().catch(undefined),
  myRating: z.enum(MY_RATING_FILTERS).optional().catch(undefined),
  files: z.enum(BOOK_FILES_FILTERS).optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});
export type BookSearch = z.infer<typeof bookSearchSchema>;

/** Search params of the author/series/tag/publisher list pages. */
export const entitySearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  sort: z.enum(["name", "count"]).optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});
export type EntitySearch = z.infer<typeof entitySearchSchema>;
export const ENTITY_PAGE_SIZE = 100;

export const FILTER_KEYS = ["author", "series", "tag", "publisher", "language", "format"] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];

export const ENTITY_FILTER_KEYS = ["author", "series", "tag", "publisher"] as const;
export type EntityFilterKey = (typeof ENTITY_FILTER_KEYS)[number];

/** Entity kind (plural, API path) for each entity filter. */
export const FILTER_KIND: Record<EntityFilterKey, CatalogEntityKind> = {
  author: "authors",
  series: "series",
  tag: "tags",
  publisher: "publishers",
};

export const PAGE_SIZE = 48;

/** True on the first page without a search query or any filter value. */
export function isUnrefinedSearch(search: BookSearch): boolean {
  return (
    (search.q ?? "").trim() === "" &&
    (search.page ?? 1) === 1 &&
    !search.author?.length &&
    !search.series?.length &&
    !search.tag?.length &&
    !search.publisher?.length &&
    !search.language?.length &&
    !search.format?.length &&
    !search.myStatus &&
    !search.myRating &&
    !search.files
  );
}

/** Sort/order the UI shows when the URL does not say. */
export function effectiveSort(search: { q?: string; sort?: string; order?: SortOrder }): {
  sort: string;
  order: SortOrder;
} {
  const hasQuery = (search.q ?? "").trim().length > 0;
  const sort = search.sort ?? (hasQuery ? "relevance" : "added");
  const order =
    search.order ?? (sort === "title" || sort === "author" || sort === "series" ? "asc" : "desc");
  return { sort, order };
}

/** API query of a catalog search: multi-value filters become repeated parameters. */
export function listQuery(search: BookSearch): Record<string, string | string[]> {
  const query: Record<string, string | string[]> = { pageSize: String(PAGE_SIZE) };
  for (const [key, value] of Object.entries(search)) {
    if (Array.isArray(value)) {
      if (value.length > 0) query[key] = value;
    } else if (value !== undefined && value !== "") {
      query[key] = String(value);
    }
  }
  return query;
}

export const catalogKeys = {
  books: ["books"] as const,
  list: (search: BookSearch) => ["books", "list", search] as const,
  book: (id: string) => ["books", "detail", id] as const,
  facets: ["catalog", "facets"] as const,
  entities: (kind: CatalogEntityKind) => [kind] as const,
};

export const bookListQuery = (search: BookSearch) =>
  queryOptions({
    queryKey: catalogKeys.list(search),
    queryFn: () =>
      call(api.books.$get({ query: listQuery(search) as never })) as Promise<BookListDto>,
    placeholderData: (previous) => previous,
  });

/** The 12 most recently added books (library home, "Recently added"). */
export const RECENT_BOOKS = 12;
export const recentBooksQuery = queryOptions({
  queryKey: [...catalogKeys.books, "recent"] as const,
  queryFn: () =>
    call(
      api.books.$get({
        query: { sort: "added", order: "desc", pageSize: String(RECENT_BOOKS) } as never,
      }),
    ) as Promise<BookListDto>,
});

export const bookQuery = (id: string) =>
  queryOptions({
    queryKey: catalogKeys.book(id),
    queryFn: () => call(api.books[":id"].$get({ param: { id } })) as Promise<BookDetailDto>,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });

export const facetsQuery = queryOptions({
  queryKey: catalogKeys.facets,
  queryFn: () => call(api.catalog.facets.$get()) as Promise<CatalogFacetsDto>,
  staleTime: 60_000,
});

export interface EntityListParams {
  q?: string;
  sort?: "name" | "count";
  page?: number;
  pageSize?: number;
}

function entityApi(kind: CatalogEntityKind) {
  return api[kind];
}

export const entityListQuery = (kind: CatalogEntityKind, params: EntityListParams = {}) =>
  queryOptions({
    queryKey: [...catalogKeys.entities(kind), "list", params] as const,
    queryFn: () => {
      const query: Record<string, string> = {};
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== "") query[key] = String(value);
      }
      return call(entityApi(kind).$get({ query: query as never })) as Promise<CatalogEntityListDto>;
    },
    placeholderData: (previous) => previous,
  });

export const entityQuery = (kind: CatalogEntityKind, id: string) =>
  queryOptions({
    queryKey: [...catalogKeys.entities(kind), "detail", id] as const,
    queryFn: () =>
      call(entityApi(kind)[":id"].$get({ param: { id } })) as Promise<CatalogEntityDto>,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });

export function coverUrl(bookId: string, size: "sm" | "md" | "original", version: number | null) {
  return `/api/books/${bookId}/cover?size=${size}${version !== null ? `&v=${version}` : ""}`;
}

export function downloadUrl(bookId: string, fileId: string) {
  return `/api/books/${bookId}/files/${fileId}/download`;
}

/** Singular route segment for the detail page of an entity kind. */
export const KIND_PATH = {
  authors: "/authors/$id",
  series: "/series/$id",
  tags: "/tags/$id",
  publishers: "/publishers/$id",
} as const;

export const KIND_FILTER: Record<CatalogEntityKind, EntityFilterKey> = {
  authors: "author",
  series: "series",
  tags: "tag",
  publishers: "publisher",
};

/**
 * Value for the "sort title" field: empty while the stored one is just derived from the title
 * ("Hobbit, The"), so changing the title re-derives it instead of keeping a stale explicit value.
 */
export function editableSortTitle(book: { title: string; sortTitle: string }): string {
  return book.sortTitle === sortTitle(book.title) ? "" : book.sortTitle;
}
